import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Trash2, MapPin, Building2, ChevronDown, Check, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { billing } from "@/lib/billingApi";
import portal from "@/lib/portalApi";

const TX = {
  en: {
    intro: "Each invoicing company issues documents in its own currency for the locations it serves. Add a company to open a new office or country.",
    add: "Add invoicing company", newTitle: "New invoicing company", newSub: "You can complete bank details, VAT and documents after creating it.",
    code: "Code (2–6 letters)", legal: "Legal name", currency: "Currency (ISO, e.g. MZN, BRL, USD)", mode: "Invoicing",
    internal: "Invoices issued here", external: "Proforma here; legal invoice in certified local software",
    locations: "Locations served", locKey: "Code", city: "City", country: "Country", address: "Address", phone: "Phone", email: "Email",
    online: "Online", addLoc: "Add location", active: "Active", inactive: "Inactive", save: "Save", create: "Create company", cancel: "Cancel",
    details: "Company details", tax: "Tax ID", vat: "Default VAT %", terms: "Payment terms (days)", validity: "Proposal validity (days)",
    bank: "Bank", holder: "Account holder", iban: "IBAN / account", swift: "SWIFT / BIC", footer: "Footer text on documents",
    prefixes: "Numbering prefixes", proposal: "Proposal", invoice: "Invoice", proforma: "Proforma", credit_note: "Credit note",
    saved: "Saved", created: "Company created", deactivate: "Deactivate this company", activate: "Reactivate this company", locHint: "Customers choose these locations in the application forms.",
  },
  pt: {
    intro: "Cada empresa faturadora emite documentos na sua moeda para os locais que serve. Adicione uma empresa para abrir um novo escritório ou país.",
    add: "Adicionar empresa faturadora", newTitle: "Nova empresa faturadora", newSub: "Pode completar dados bancários, IVA e documentos depois de criar.",
    code: "Código (2–6 letras)", legal: "Denominação social", currency: "Moeda (ISO, ex. MZN, BRL, USD)", mode: "Faturação",
    internal: "Faturas emitidas aqui", external: "Proforma aqui; fatura legal em software local certificado",
    locations: "Locais servidos", locKey: "Código", city: "Cidade", country: "País", address: "Morada", phone: "Telefone", email: "Email",
    online: "Online", addLoc: "Adicionar local", active: "Ativa", inactive: "Inativa", save: "Guardar", create: "Criar empresa", cancel: "Cancelar",
    details: "Dados da empresa", tax: "NIF", vat: "IVA por defeito %", terms: "Prazo de pagamento (dias)", validity: "Validade da proposta (dias)",
    bank: "Banco", holder: "Titular", iban: "IBAN / conta", swift: "SWIFT / BIC", footer: "Rodapé dos documentos",
    prefixes: "Prefixos de numeração", proposal: "Proposta", invoice: "Fatura", proforma: "Proforma", credit_note: "Nota de crédito",
    saved: "Guardado", created: "Empresa criada", deactivate: "Desativar esta empresa", activate: "Reativar esta empresa", locHint: "Os clientes escolhem estes locais nos formulários de candidatura.",
  },
  fr: {
    intro: "Chaque société de facturation émet ses documents dans sa devise pour les lieux qu'elle dessert. Ajoutez une société pour ouvrir un nouveau bureau ou pays.",
    add: "Ajouter une société de facturation", newTitle: "Nouvelle société de facturation", newSub: "Vous compléterez banque, TVA et documents après la création.",
    code: "Code (2–6 lettres)", legal: "Raison sociale", currency: "Devise (ISO, ex. MZN, BRL, USD)", mode: "Facturation",
    internal: "Factures émises ici", external: "Proforma ici ; facture légale dans un logiciel local certifié",
    locations: "Lieux desservis", locKey: "Code", city: "Ville", country: "Pays", address: "Adresse", phone: "Téléphone", email: "Email",
    online: "En ligne", addLoc: "Ajouter un lieu", active: "Active", inactive: "Inactive", save: "Enregistrer", create: "Créer la société", cancel: "Annuler",
    details: "Informations de la société", tax: "N° fiscal", vat: "TVA par défaut %", terms: "Délai de paiement (jours)", validity: "Validité des propositions (jours)",
    bank: "Banque", holder: "Titulaire", iban: "IBAN / compte", swift: "SWIFT / BIC", footer: "Pied de page des documents",
    prefixes: "Préfixes de numérotation", proposal: "Proposition", invoice: "Facture", proforma: "Proforma", credit_note: "Avoir",
    saved: "Enregistré", created: "Société créée", deactivate: "Désactiver cette société", activate: "Réactiver cette société", locHint: "Les clients choisissent ces lieux dans les formulaires de demande.",
  },
};

const err = (e) => toast.error(e?.response?.data?.detail?.toString?.() || "Error");
const slug = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30);
const inp = "h-9 bg-slate-950 border-slate-700";

const Lbl = ({ label, children, className = "" }) => (
  <label className={`block text-xs text-slate-400 ${className}`}>{label}<div className="mt-1">{children}</div></label>
);

const LocationsEditor = ({ value, onChange, tx, disabled }) => {
  const set = (i, k, v) => onChange(value.map((l, j) => (j === i ? { ...l, [k]: v, ...(k === "city" && !l._keyTouched && !l._saved ? { key: slug(v) } : {}) } : l)));
  return (
    <div className="space-y-2">
      {value.map((l, i) => (
        <div key={i} className="grid gap-2 rounded-xl border border-slate-800 p-3 sm:grid-cols-[1.1fr_0.8fr_1fr_auto]" data-testid={`location-row-${i}`}>
          <Lbl label={tx.city}><Input disabled={disabled} className={inp} value={l.city || ""} onChange={(e) => set(i, "city", e.target.value)} /></Lbl>
          <Lbl label={tx.locKey}><Input disabled={disabled || l._saved} className={`${inp} font-mono text-xs`} value={l.key || ""} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, key: slug(e.target.value), _keyTouched: true } : x)))} /></Lbl>
          <Lbl label={tx.country}><Input disabled={disabled} className={inp} value={l.country || ""} onChange={(e) => set(i, "country", e.target.value)} /></Lbl>
          <div className="flex items-end gap-2">
            <button type="button" disabled={disabled} onClick={() => set(i, "active", l.active === false)} title={l.active === false ? tx.inactive : tx.active}
              className={`h-9 rounded-lg px-2.5 text-xs font-medium ${l.active === false ? "bg-slate-800 text-slate-400" : "bg-green-500/15 text-green-300"}`}>
              {l.active === false ? tx.inactive : tx.active}
            </button>
            {!disabled && <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="h-9 rounded-lg px-2 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
          </div>
          {!l.online && (
            <>
              <Lbl label={tx.address} className="sm:col-span-2"><Input disabled={disabled} className={inp} value={l.address || ""} onChange={(e) => set(i, "address", e.target.value)} /></Lbl>
              <Lbl label={tx.phone}><Input disabled={disabled} className={inp} value={l.phone || ""} onChange={(e) => set(i, "phone", e.target.value)} /></Lbl>
              <Lbl label={tx.email}><Input disabled={disabled} className={inp} value={l.email || ""} onChange={(e) => set(i, "email", e.target.value)} /></Lbl>
            </>
          )}
        </div>
      ))}
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" className="rounded-full border-slate-700" onClick={() => onChange([...value, { key: "", city: "", country: "", active: true }])} data-testid="add-location">
            <MapPin className="h-3.5 w-3.5 mr-1" />{tx.addLoc}
          </Button>
          {!value.some((l) => l.key === "online") && (
            <Button type="button" size="sm" variant="outline" className="rounded-full border-slate-700" onClick={() => onChange([...value, { key: "online", city: "Online", online: true, active: true }])}>
              <Globe2 className="h-3.5 w-3.5 mr-1" />{tx.online}
            </Button>
          )}
        </div>
      )}
      <p className="text-[11px] text-slate-500">{tx.locHint}</p>
    </div>
  );
};

const strip = (locs) => locs.filter((l) => l.key).map(({ _saved, _keyTouched, ...l }) => l);

const CompanyCard = ({ entity, tx, isAdmin, onSaved, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [d, setD] = useState(entity);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setD({ ...entity, locations: (entity.locations || []).map((l) => ({ ...l, _saved: true })) }); }, [entity]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setIn = (obj, k, v) => setD((x) => ({ ...x, [obj]: { ...(x[obj] || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await billing.updateEntity(entity.code, {
        legal_name: d.legal_name, trade_name: d.trade_name, tax_id: d.tax_id, address: d.address, email: d.email, phone: d.phone,
        vat_rate: Number(d.vat_rate || 0), payment_terms_days: Number(d.payment_terms_days || 7), proposal_validity_days: Number(d.proposal_validity_days || 15),
        bank: d.bank, footer: d.footer, prefixes: d.prefixes, fiscal_mode: d.fiscal_mode, active: d.active !== false, locations: strip(d.locations || []),
      });
      toast.success(tx.saved); onSaved(r);
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  const F = (k, label, type) => <Lbl label={label}><Input type={type || "text"} disabled={!isAdmin} className={inp} value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></Lbl>;
  const active = d.active !== false;
  return (
    <div className={`rounded-2xl border bg-slate-900/40 ${active ? "border-slate-800" : "border-dashed border-slate-700 opacity-80"}`} data-testid={`entity-${entity.code}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-4 p-4 text-left">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-venka-blue/10 text-sm font-bold text-venka-blue">{entity.code}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-slate-100">{entity.legal_name}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
            <span className="rounded-full bg-slate-800 px-2 py-0.5 font-mono">{entity.currency}</span>
            {(entity.locations || []).map((l) => (
              <span key={l.key} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${l.active === false ? "bg-slate-800 line-through" : "bg-green-500/10 text-green-300"}`}>
                <MapPin className="h-3 w-3" />{l.city || l.key}
              </span>
            ))}
            {!active && <span className="rounded-full bg-slate-800 px-2 py-0.5">{tx.inactive}</span>}
          </span>
        </span>
        <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-5 border-t border-slate-800 p-4 md:p-5">
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-200">{tx.locations}</h4>
            <LocationsEditor value={d.locations || []} onChange={(v) => set("locations", v)} tx={tx} disabled={!isAdmin} />
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-200">{tx.details}</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {F("legal_name", tx.legal)}{F("tax_id", tx.tax)}{F("address", tx.address)}{F("email", tx.email)}{F("phone", tx.phone)}
              {F("vat_rate", tx.vat, "number")}{F("payment_terms_days", tx.terms, "number")}{F("proposal_validity_days", tx.validity, "number")}
              <Lbl label={tx.mode}>
                <select disabled={!isAdmin} className="h-9 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm" value={d.fiscal_mode} onChange={(e) => set("fiscal_mode", e.target.value)}>
                  <option value="internal">{tx.internal}</option><option value="external">{tx.external}</option>
                </select>
              </Lbl>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[["bank_name", tx.bank], ["account_holder", tx.holder], ["iban", tx.iban], ["swift", tx.swift]].map(([k, l]) => (
              <Lbl key={k} label={l}><Input disabled={!isAdmin} className={inp} value={d.bank?.[k] ?? ""} onChange={(e) => setIn("bank", k, e.target.value)} /></Lbl>
            ))}
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-200">{tx.prefixes}</h4>
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              {["proposal", "invoice", "proforma", "credit_note"].map((k) => (
                <Lbl key={k} label={tx[k]}><Input disabled={!isAdmin} className={`${inp} font-mono text-xs`} value={d.prefixes?.[k] ?? ""} onChange={(e) => setIn("prefixes", k, e.target.value)} /></Lbl>
              ))}
            </div>
          </div>
          {F("footer", tx.footer)}
          {isAdmin && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button type="button" onClick={() => set("active", !active)} className="text-xs text-slate-500 hover:text-slate-200">
                {active ? tx.deactivate : tx.activate}
              </button>
              <Button onClick={save} disabled={saving} className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" data-testid={`entity-save-${entity.code}`}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}{tx.save}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const CompaniesManager = () => {
  const { i18n } = useTranslation();
  const tx = TX[(i18n.language || "en").slice(0, 2)] || TX.en;
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justCreated, setJustCreated] = useState("");
  const load = useCallback(() => billing.entities().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await portal.post("/billing/entities", { ...creating, locations: strip(creating.locations) });
      toast.success(tx.created); setCreating(null); setJustCreated(r.data.code); load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  if (!rows) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  return (
    <div className="space-y-4" data-testid="companies-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-400">{tx.intro}</p>
        {isAdmin && (
          <Button onClick={() => setCreating({ code: "", legal_name: "", currency: "", fiscal_mode: "internal", locations: [{ key: "", city: "", country: "", active: true }] })}
            className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" data-testid="entity-add">
            <Plus className="h-4 w-4 mr-1.5" />{tx.add}
          </Button>
        )}
      </div>
      {rows.map((e) => (
        <CompanyCard key={e.code} entity={e} tx={tx} isAdmin={isAdmin} defaultOpen={e.code === justCreated}
          onSaved={(r) => setRows((rs) => rs.map((x) => (x.code === r.code ? r : x)))} />
      ))}

      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="entity-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-venka-blue" />{tx.newTitle}</DialogTitle>
            <DialogDescription>{tx.newSub}</DialogDescription>
          </DialogHeader>
          {creating && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Lbl label={tx.code}><Input className={`${inp} uppercase font-mono`} maxLength={6} value={creating.code} onChange={(e) => setCreating({ ...creating, code: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })} data-testid="entity-new-code" /></Lbl>
                <Lbl label={tx.legal} className="sm:col-span-2"><Input className={inp} value={creating.legal_name} onChange={(e) => setCreating({ ...creating, legal_name: e.target.value })} data-testid="entity-new-legal" /></Lbl>
                <Lbl label={tx.currency}><Input className={`${inp} uppercase font-mono`} maxLength={3} value={creating.currency} onChange={(e) => setCreating({ ...creating, currency: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })} data-testid="entity-new-currency" /></Lbl>
                <Lbl label={tx.mode} className="sm:col-span-2">
                  <select className="h-9 w-full rounded-md border border-slate-700 bg-slate-950 px-2 text-sm" value={creating.fiscal_mode} onChange={(e) => setCreating({ ...creating, fiscal_mode: e.target.value })}>
                    <option value="internal">{tx.internal}</option><option value="external">{tx.external}</option>
                  </select>
                </Lbl>
              </div>
              <div>
                <h4 className="mb-2 text-sm font-semibold">{tx.locations}</h4>
                <LocationsEditor value={creating.locations} onChange={(v) => setCreating({ ...creating, locations: v })} tx={tx} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(null)}>{tx.cancel}</Button>
            <Button onClick={create} disabled={busy || !creating?.code || !creating?.legal_name || (creating?.currency || "").length !== 3}
              className="rounded-xl bg-venka-blue text-white hover:bg-venka-blue-dark" data-testid="entity-create">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{tx.create}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesManager;
